import { useEffect, useState } from 'react';
import { streamLandingPromptPhrases } from '@/api/landing';
import { detectGoogleAdsKeyword } from '@/lib/googleAdsKeyword';

type LandingPromptContext = {
  landingKey: string;
  pageTitle: string;
  pageSubtitle?: string;
  pageCtaHint?: string;
  defaultPhrases: string[];
};

type LandingPromptResult = {
  phrases: string[];
  isPersonalized: boolean;
  adKeyword: string | null;
  isLoading: boolean;
};

const SESSION_CACHE_PREFIX = 'landingPromptPhrases:';

function getSessionCacheKey(landingKey: string, keyword: string): string {
  return `${SESSION_CACHE_PREFIX}${landingKey}:${keyword.toLowerCase()}`;
}

function getCachedPhrases(landingKey: string, keyword: string): string[] | null {
  if (typeof window === 'undefined') return null;

  const sessionKey = getSessionCacheKey(landingKey, keyword);
  const cached = sessionStorage.getItem(sessionKey);
  if (!cached) return null;

  try {
    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    sessionStorage.removeItem(sessionKey);
    return null;
  }
}

function getInitialPromptState(landingKey: string, defaultPhrases: string[]): LandingPromptResult {
  const keyword = detectGoogleAdsKeyword();
  if (!keyword) {
    return {
      phrases: defaultPhrases,
      isPersonalized: false,
      adKeyword: null,
      isLoading: false,
    };
  }

  const cachedPhrases = getCachedPhrases(landingKey, keyword);
  if (cachedPhrases) {
    return {
      phrases: cachedPhrases,
      isPersonalized: true,
      adKeyword: keyword,
      isLoading: false,
    };
  }

  return {
    phrases: [],
    isPersonalized: false,
    adKeyword: keyword,
    isLoading: true,
  };
}

export function useLandingPromptPhrases({
  landingKey,
  pageTitle,
  pageSubtitle,
  pageCtaHint,
  defaultPhrases,
}: LandingPromptContext): LandingPromptResult {
  const [phrases, setPhrases] = useState(() => getInitialPromptState(landingKey, defaultPhrases).phrases);
  const [isPersonalized, setIsPersonalized] = useState(() => getInitialPromptState(landingKey, defaultPhrases).isPersonalized);
  const [adKeyword, setAdKeyword] = useState<string | null>(() => getInitialPromptState(landingKey, defaultPhrases).adKeyword);
  const [isLoading, setIsLoading] = useState(() => getInitialPromptState(landingKey, defaultPhrases).isLoading);

  useEffect(() => {
    const initialState = getInitialPromptState(landingKey, defaultPhrases);
    setPhrases(initialState.phrases);
    setIsPersonalized(initialState.isPersonalized);
    setAdKeyword(initialState.adKeyword);
    setIsLoading(initialState.isLoading);

    const keyword = initialState.adKeyword;
    if (!keyword || initialState.isPersonalized || !initialState.isLoading) return;

    const sessionKey = getSessionCacheKey(landingKey, keyword);
    let cancelled = false;
    let streamedPhrases: string[] = [];
    const controller = streamLandingPromptPhrases(
      {
        utm_term: keyword,
        landing_key: landingKey,
        page_title: pageTitle,
        page_subtitle: pageSubtitle || '',
        page_cta_hint: pageCtaHint || '',
      },
      {
        onPhrase: (_phrase, phrasesFromStream) => {
          if (cancelled || phrasesFromStream.length === 0) {
            return;
          }
          streamedPhrases = phrasesFromStream;
          setPhrases(phrasesFromStream);
          setIsPersonalized(true);
          setIsLoading(false);
        },
        onDone: (result) => {
          if (cancelled) {
            return;
          }

          const finalPhrases = Array.isArray(result.phrases) && result.phrases.length > 0
            ? result.phrases
            : streamedPhrases;

          if (finalPhrases.length === 0) {
            setPhrases(defaultPhrases);
            setIsPersonalized(false);
            setIsLoading(false);
            return;
          }

          setPhrases(finalPhrases);
          setIsPersonalized(true);
          setIsLoading(false);

          if (typeof window !== 'undefined') {
            sessionStorage.setItem(sessionKey, JSON.stringify(finalPhrases));
          }
        },
        onError: () => {
          if (cancelled) {
            return;
          }

          if (streamedPhrases.length > 0) {
            setPhrases(streamedPhrases);
            setIsPersonalized(true);
            setIsLoading(false);
            return;
          }

          setPhrases(defaultPhrases);
          setIsPersonalized(false);
          setIsLoading(false);
        },
      },
    );

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [landingKey, pageTitle, pageSubtitle, pageCtaHint, defaultPhrases]);

  return { phrases, isPersonalized, adKeyword, isLoading };
}
